/**
 * MaplibreGL-unabhängige Evaluierung der Raumplanung `fill-color`-Expression.
 *
 * Die Kartenfarben der Raumplanung werden serverseitig als MapLibre
 * `fill-color`-Expression des `data-fill`-Layers erzeugt (siehe
 * `atlas-server/src/services/RaumplanungService/styleFunctions/*.ts`). Um die
 * Balken in der Feature-Liste mit denselben Farben wie auf der Karte zu
 * färben, muss diese Expression clientseitig für einen Featurewert zu einer
 * Farbe aufgelöst werden.
 *
 * Diese Datei enthält eine schlanke, eigenständige Evaluierungsfunktion, die
 * NUR die tatsächlich vom Server erzeugten Expressionstypen versteht und
 * bewusst KEINE Abhängigkeit von maplibregl / @maplibre/maplibre-gl-style-spec
 * hat (kein `createExpression`).
 *
 * Unterstützte Expressionstypen:
 * - `["get", "value"]` → der Featurewert
 * - `["case", cond1, val1, cond2, val2, ..., fallback]` → erste zutreffende Bedingung
 * - Vergleichsoperatoren `["==", a, b]`, `["<", a, b]`, `[">", a, b]` (inkl. null)
 * - `["interpolate", ["linear"], input, stop1, color1, stop2, color2, ...]` → lineare Farbinterpolation
 * - `["step", input, base, stop1, output1, ...]` → diskrete Stufen (defensiv)
 * - `["match", input, label1, output1, ..., fallback]` → diskrete Zuordnung (defensiv)
 * - Arithmetik `["-", a, b]` (plusminus/diverging-Sonderfälle, defensiv)
 * - Literale (Zahlen, Farb-Strings, null)
 */

/** Eine kompilierte Evaluierungsfunktion: Featurewert → Farb-String. */
export type ColorExpressionEvaluator = (value: number | null) => string

/** Fallback-Farbe, falls die Expression nicht ausgewertet werden kann. */
const FALLBACK_COLOR = "#888888"

/**
 * Erstellt eine kompilierte Evaluierungsfunktion für eine MapLibre
 * `fill-color`-Expression. Die Expression wird einmal geparst; die
 * resultierende Funktion kann dann für viele Werte effizient aufgerufen
 * werden (wichtig bei großen Feature-Listen).
 *
 * @param expression Die `fill-color`-Expression (z. B. aus
 *                   `style.layers["data-fill"].paint["fill-color"]`).
 * @returns Eine Funktion `(value) => colorString`.
 */
export function createColorExpressionEvaluator(expression: any): ColorExpressionEvaluator {
    // Pre-compile the expression into a closure for performance.
    const compiled = compile(expression)
    return (value: number | null) => {
        const result = compiled(value)
        if (typeof result === "string") {
            return result
        }
        return FALLBACK_COLOR
    }
}

/**
 * Kompiliert eine Expression in eine Funktion `(value) => any`.
 * Literale (Zahlen, Strings, null) werden direkt zurückgegeben.
 */
function compile(expr: any): (value: number | null) => any {
    if (!Array.isArray(expr)) {
        // Literal (Zahl, Farb-String, null, ...)
        return () => expr
    }

    const op = expr[0]

    switch (op) {
        case "get": {
            // ["get", "value"] → der Featurewert
            return (value) => value
        }

        case "case": {
            // ["case", cond1, val1, cond2, val2, ..., fallback]
            const branches: Array<{ cond: (v: number | null) => any, val: (v: number | null) => any }> = []
            for (let i = 1; i < expr.length - 1; i += 2) {
                branches.push({ cond: compile(expr[i]), val: compile(expr[i + 1]) })
            }
            const fallback = compile(expr[expr.length - 1])
            return (value) => {
                for (const b of branches) {
                    if (b.cond(value)) {
                        return b.val(value)
                    }
                }
                return fallback(value)
            }
        }

        case "interpolate": {
            // ["interpolate", ["linear"], input, stop1, color1, stop2, color2, ...]
            const input = compile(expr[2])
            const stops: Array<{ stop: number, color: string }> = []
            for (let i = 3; i < expr.length; i += 2) {
                stops.push({ stop: expr[i], color: expr[i + 1] })
            }
            return (value) => {
                const inputValue = input(value)
                if (inputValue === null || inputValue === undefined) {
                    return null
                }
                return interpolateColor(inputValue, stops)
            }
        }

        case "step": {
            // ["step", input, base, stop1, output1, stop2, output2, ...]
            const input = compile(expr[1])
            const base = compile(expr[2])
            const stops: Array<{ stop: number, output: any }> = []
            for (let i = 3; i < expr.length; i += 2) {
                stops.push({ stop: expr[i], output: compile(expr[i + 1]) })
            }
            return (value) => {
                const inputValue = input(value)
                if (inputValue === null || inputValue === undefined) {
                    return null
                }
                let result = base(value)
                for (const s of stops) {
                    if (inputValue >= s.stop) {
                        result = s.output(value)
                    }
                }
                return result
            }
        }

        case "match": {
            // ["match", input, label1, output1, label2, output2, ..., fallback]
            const input = compile(expr[1])
            const branches: Array<{ labels: any[], output: (v: number | null) => any }> = []
            let fallback: (v: number | null) => any = () => FALLBACK_COLOR
            for (let i = 2; i < expr.length; i += 2) {
                const key = expr[i]
                if (Array.isArray(key)) {
                    branches.push({ labels: key, output: compile(expr[i + 1]) })
                } else {
                    // Last entry is the fallback
                    fallback = compile(expr[i])
                    break
                }
            }
            return (value) => {
                const inputValue = input(value)
                for (const b of branches) {
                    if (b.labels.includes(inputValue)) {
                        return b.output(value)
                    }
                }
                return fallback(value)
            }
        }

        case "==": {
            const a = compile(expr[1])
            const b = compile(expr[2])
            return (value) => a(value) === b(value)
        }

        case "<": {
            const a = compile(expr[1])
            const b = compile(expr[2])
            return (value) => a(value) < b(value)
        }

        case ">": {
            const a = compile(expr[1])
            const b = compile(expr[2])
            return (value) => a(value) > b(value)
        }

        case "-": {
            const a = compile(expr[1])
            const b = compile(expr[2])
            return (value) => a(value) - b(value)
        }

        case "literal": {
            const literal = expr[1]
            return () => literal
        }

        default:
            // Unbekannter Expressionstyp → defensiv auf Fallback-Farbe.
            return () => FALLBACK_COLOR
    }
}

/**
 * Interpoliert eine Farbe linear zwischen den Stops für einen Eingabewert.
 * Unterstützt Hex-Farben ("#RRGGBB" / "#RGB"). Werte außerhalb des
 * Stop-Bereichs werden auf den ersten bzw. letzten Stop geklemmt.
 */
function interpolateColor(input: number, stops: Array<{ stop: number, color: string }>): string {
    if (stops.length === 0) {
        return FALLBACK_COLOR
    }
    if (input <= stops[0].stop) {
        return stops[0].color
    }
    const last = stops[stops.length - 1]
    if (input >= last.stop) {
        return last.color
    }

    for (let i = 0; i < stops.length - 1; i++) {
        const a = stops[i]
        const b = stops[i + 1]
        if (input >= a.stop && input <= b.stop) {
            const t = b.stop === a.stop ? 0 : (input - a.stop) / (b.stop - a.stop)
            return lerpColor(a.color, b.color, t)
        }
    }

    return last.color
}

/** Lineare Interpolation zwischen zwei Hex-Farben mit Faktor t in [0, 1]. */
function lerpColor(colorA: string, colorB: string, t: number): string {
    const rgbA = parseHexColor(colorA)
    const rgbB = parseHexColor(colorB)
    if (!rgbA || !rgbB) {
        return t < 0.5 ? colorA : colorB
    }
    const r = Math.round(rgbA[0] + (rgbB[0] - rgbA[0]) * t)
    const g = Math.round(rgbA[1] + (rgbB[1] - rgbA[1]) * t)
    const b = Math.round(rgbA[2] + (rgbB[2] - rgbA[2]) * t)
    return `rgb(${r}, ${g}, ${b})`
}

/** Parst eine Hex-Farbe ("#RRGGBB" oder "#RGB") in [r, g, b]. */
function parseHexColor(color: string): [number, number, number] | null {
    if (typeof color !== "string") {
        return null
    }
    let hex = color.trim()
    if (hex.startsWith("#")) {
        hex = hex.slice(1)
    }
    if (hex.length === 3) {
        hex = hex.split("").map((c) => c + c).join("")
    }
    if (hex.length !== 6) {
        return null
    }
    const int = parseInt(hex, 16)
    if (Number.isNaN(int)) {
        return null
    }
    return [(int >> 16) & 255, (int >> 8) & 255, int & 255]
}
